import * as Sentry from '@sentry/react';
import { makeStateStore } from '@playcastdotio/StateStore';
import type { PlaycastGamepadId, StateStore } from '@playcastdotio/CoreTypes';
import type { PeerMetadata } from '@playcastdotio/Messaging';
import { CoordinatorEventImpl } from '../_types/events';
import type { PeerState, PeerEvents, Player } from './stores';
import type { MediaCoordinator } from '../media/MediaCoordinator';
import type { SessionCoordinator } from '../session/SessionCoordinator';
import type {
  ConnectedPeer,
  PeerType,
  PeerPermissions,
  IPeerConnectionFactory,
  ISignalingService,
  SerializableMessage,
  PeerIdAssignedData,
} from './types';
import { DEFAULT_PERMISSIONS } from './types';
import { PeerConnectionManager } from './managers/PeerConnectionManager';
import { PeerTypeRegistry } from './managers/PeerTypeRegistry';
import { PeerStatsManager, type PeerStats } from './managers/PeerStatsManager';
import { InputControlManager } from './managers/InputControlManager';
import type { InputConfigStores, GamepadConfigState } from './stores/inputConfigStores';
import type { GamepadManager } from './managers/GamepadManager';
import type { ActivityTracker } from './managers/ActivityTracker';
import { fetchIceServersFromApi } from '../../services/peer/iceServerFetch';
import { BASE_STREAM_CAMERA } from '../media/cameraNames';

export type PeerCoordinatorConfig = {
  iceTransportPolicy?: RTCIceTransportPolicy;
  /**
   * Runs immediately before opening ShadowCam for an admin offer. Must refresh Playjector display→camera
   * binding and wait long enough for setSource (Electron uses ~500ms stagger) or the capture track can stay
   * on the ShadowCam placeholder forever (solid green in the admin dashboard).
   */
  beforeAdminMediaCapture?: () => Promise<void>;
  /**
   * Called reactively when a peer needs a shadowcam that hasn't been activated yet
   * (e.g. ShadowCam01 for mobile). Sends startStopToggle via Playjector IPC.
   * Resolves once Playjector acknowledges or after a reasonable timeout.
   * A rejected activation is non-fatal for legacy pavilion/admin capture: capture
   * still proceeds using the existing fallback behavior. Game instance sessions surface
   * their readiness state through SessionCastResult instead of aborting the offer.
   */
  activateShadowcam?: (shadowcamName: string) => Promise<void>;
  /**
   * Android multi-instance host Phase 5: late-bound resolver for the active
   * android launch's streamId (`GameInstanceLaunchManager.getActiveAndroidStreamId`,
   * wired in init.ts). While it resolves, instance session cameras are activated
   * BY capture (streams model) — `activateShadowcam` is skipped for session
   * peers, so the host never sends explicit bind/activate messages on the
   * android path. Legacy/mumu sessions keep the explicit activation.
   */
  getActiveAndroidStreamId?: () => string | undefined;
};

/**
 * Resolves a game instance session for an incoming player offer. The
 * implementation bridges PeerCoordinator's instance-peer map to the
 * GameInstanceLaunchManager's session records.
 */
export type GameInstanceSessionResolver = {
  getSessionForInstance(gameInstanceIndex: number): NonNullable<PeerMetadata['playjectorSession']> | null;
};

/**
 * Occupancy seam for game instance sessions (pool-match-lifecycle Phase 2,
 * item 3). Wired to the MatchLifecycleManager in production. Both methods
 * return the session's player count after the change so PeerCoordinator can
 * apply capture-ownership rules: session capture stops only when the count
 * reaches 0, not on any single player disconnect.
 */
export type GameInstanceOccupancyTracker = {
  playerJoinedSession(sessionId: string): number;
  playerLeftSession(sessionId: string): number;
};

/**
 * PeerCoordinator - Orchestrates all WebRTC peer connections.
 *
 * Replaces the legacy host.ts module. Manages connections for all peer types
 * (player, admin, remoteHost) using injectable factories for testability.
 *
 * Uses dedicated events per peer type for best DX.
 * Subscribes to MediaCoordinator.tracksChanged to push track updates.
 */
const DIAG = '[AUDIO-BUG-12009:PeerCoordinator]';
const DEFAULT_PREWARM_INTERVAL_MS = 300_000; // 5 minutes
// Android multi-instance Phase 5 (coordinated rename): the desktop/base camera
// comes from the single shared constant, mirrored to C++ Constants::BASE_STREAM_CAMERA.
const DESKTOP_SHADOWCAM = BASE_STREAM_CAMERA;
/**
 * Legacy mobile-web camera. Nominally aliases instance 0's camera under the
 * android naming contract (`ShadowCam{k+1:02d}`), but the two never coexist:
 * `selectShadowcam` only returns it for peers WITHOUT `playjectorSession`
 * metadata (both call sites gate on that), and on an active android launch
 * every player peer joins through an instance peer that carries the session
 * camera. A sessionless mobile viewer is a legacy single-stream flow that
 * does not run concurrently with android multi-instance campaigns.
 */
const MOBILE_SHADOWCAM = 'ShadowCam01';
/** Admin dashboard uses the same desktop ShadowCam as the player stream (the base stream camera). */
const ADMIN_SHADOWCAM = DESKTOP_SHADOWCAM;

export class PeerCoordinator {
  private peerStore: StateStore<PeerState>;
  private connectionManager: PeerConnectionManager;
  private typeRegistry: PeerTypeRegistry;
  private statsManager: PeerStatsManager;
  private inputControl: InputControlManager;
  private mediaCoordinator: MediaCoordinator | null = null;
  private signalingService: ISignalingService | null = null;
  private iceTransportPolicy: RTCIceTransportPolicy = 'all';
  private beforeAdminMediaCapture: (() => Promise<void>) | null = null;
  private activateShadowcam: ((shadowcamName: string) => Promise<void>) | null = null;
  private getActiveAndroidStreamId: (() => string | undefined) | null = null;
  private activatedShadowcams = new Set<string>();
  private unsubscribers: Array<() => void> = [];
  private preWarmRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private playerCounter = 0;
  private peerNameMap = new Map<string, string>();
  private previousVideoTrack: MediaStreamTrack | null = null;
  private previousAudioTrack: MediaStreamTrack | null = null;
  private sessionResolver: GameInstanceSessionResolver | null = null;
  private occupancyTracker: GameInstanceOccupancyTracker | null = null;
  /** Maps instance peerId → gameInstanceIndex for N-peer routing. */
  private instancePeerIds = new Map<string, number>();
  /** Resolves an instance's HOST peer id from its index (GameInstancePeerManager in production). */
  private gameInstancePeerIdResolver: ((gameInstanceIndex: number) => string | undefined) | null = null;
  /**
   * player peerId → the local HOST peer id its signaling is sent FROM. v2
   * session connections answer from the instance peer the player offered to —
   * a v2 host never holds the v1 global host peer (its auth-time request only
   * fires for an already-streaming host, and the v1 startStream request is
   * gated off), so keying outbound signaling off the global id silently
   * dropped every answer.
   */
  private readonly connectionFromIds = new Map<string, string>();

  public events: PeerEvents = {
    onPlayerJoined: new CoordinatorEventImpl(),
    onPlayerLeft: new CoordinatorEventImpl(),
    onAdminJoined: new CoordinatorEventImpl(),
    onAdminLeft: new CoordinatorEventImpl(),
    onRemoteHostJoined: new CoordinatorEventImpl(),
    onRemoteHostLeft: new CoordinatorEventImpl(),
    playerCountChanged: new CoordinatorEventImpl(),
    hostPeerIdChanged: new CoordinatorEventImpl(),
    remoteHostPeerIdChanged: new CoordinatorEventImpl(),
    permissionsChanged: new CoordinatorEventImpl(),
    dataReceived: new CoordinatorEventImpl(),
    pauseStateChanged: new CoordinatorEventImpl(),
    gamepadSlotsChanged: new CoordinatorEventImpl(),
    afkStateChanged: new CoordinatorEventImpl(),
  };

  constructor(connectionFactory: IPeerConnectionFactory) {
    this.peerStore = makeStateStore<PeerState>({
      peers: {},
      players: {},
      playerCount: 0,
      hostPeerId: undefined,
      remoteHostPeerId: undefined,
      streamStatus: 'idle',
    });

    this.connectionManager = new PeerConnectionManager(connectionFactory, {
      onPeerConnected: (peerId, peerType, metadata) => this.handlePeerConnected(peerId, peerType, metadata),
      onPeerDisconnected: (peerId, peerType) => this.handlePeerDisconnected(peerId, peerType),
      onSignalGenerated: (peerId, description) => this.handleSignalGenerated(peerId, description),
      onIceCandidatesGenerated: (peerId, candidates) => this.handleIceCandidatesGenerated(peerId, candidates),
      onRemoteStream: (peerId, stream) => this.handleRemoteStream(peerId, stream),
      onDataChannelMessage: (peerId, data) => this.handleDataChannelMessage(peerId, data),
    });

    this.typeRegistry = new PeerTypeRegistry();
    this.statsManager = new PeerStatsManager();
    this.inputControl = new InputControlManager(this.statsManager, {
      pauseStateChanged: this.events.pauseStateChanged,
      gamepadSlotsChanged: this.events.gamepadSlotsChanged,
      afkStateChanged: this.events.afkStateChanged,
    });

    this.registerDefaultHandlers();
  }

  public async init(
    sessionCoordinator?: SessionCoordinator,
    mediaCoordinator?: MediaCoordinator,
    signalingService?: ISignalingService,
    config?: PeerCoordinatorConfig,
  ): Promise<void> {
    console.log('[PeerCoordinator] Initializing...');

    this.mediaCoordinator = mediaCoordinator ?? null;
    this.signalingService = signalingService ?? null;
    this.iceTransportPolicy = config?.iceTransportPolicy ?? 'all';
    this.beforeAdminMediaCapture = config?.beforeAdminMediaCapture ?? null;
    this.activateShadowcam = config?.activateShadowcam ?? null;
    this.getActiveAndroidStreamId = config?.getActiveAndroidStreamId ?? null;

    if (sessionCoordinator?.events?.streamStarted) {
      const unsub = sessionCoordinator.events.streamStarted.addListener(() => {
        console.log('[PeerCoordinator] Stream started, ensuring media capture');
        this.ensureMediaCapture();
      });
      this.unsubscribers.push(unsub);
    }

    if (sessionCoordinator?.events?.streamStopped) {
      const unsub = sessionCoordinator.events.streamStopped.addListener(() => {
        console.log('[PeerCoordinator] Stream stopped, disconnecting all peers');
        this.updateHostPeerId(undefined);
        this.clearAllPlayers();
        this.disconnectAllPeers('host:stream_stopped');
        this.mediaCoordinator?.stopAllCapture();
      });
      this.unsubscribers.push(unsub);
    }

    if (mediaCoordinator) {
      const unsub = mediaCoordinator.events.tracksChanged.addListener(({ videoTrack, audioTrack }) => {
        this.handleTracksChanged(videoTrack, audioTrack);
      });
      this.unsubscribers.push(unsub);
    }

    if (this.signalingService) {
      const unsub = this.signalingService.onSignalingMessage((msg) => {
        this.handleSignalingMessage(msg);
      });
      this.unsubscribers.push(unsub);

      const unsubPeerId = this.signalingService.onPeerIdAssigned((data) => {
        this.handlePeerIdAssigned(data);
      });
      this.unsubscribers.push(unsubPeerId);
    }

    console.log('[PeerCoordinator] Initialized');
  }

  public destroy(): void {
    console.log('[PeerCoordinator] Destroying...');
    this.stopPreWarmCycle();
    for (const unsub of this.unsubscribers) {
      unsub();
    }
    this.unsubscribers = [];
    this.connectionFromIds.clear();
    this.connectionManager.destroyAll();
    this.statsManager.destroy();
    this.typeRegistry.destroy();
    console.log('[PeerCoordinator] Destroyed');
  }

  // ========================================
  // Public API - Pre-Warm Lifecycle
  // ========================================

  /**
   * Begin a periodic cycle: fetch ICE credentials, pre-warm an RTCPeerConnection,
   * and refresh every `intervalMs` to keep TURN credentials fresh.
   * The pre-warmed connection is purely local -- no server-side state changes.
   */
  public async startPreWarmCycle(intervalMs = DEFAULT_PREWARM_INTERVAL_MS): Promise<void> {
    this.stopPreWarmCycle();
    await this.refreshPreWarm();

    this.preWarmRefreshTimer = setInterval(async () => {
      await this.refreshPreWarm();
    }, intervalMs);

    console.log(`[PeerCoordinator] Pre-warm cycle started (interval=${intervalMs}ms)`);
  }

  public stopPreWarmCycle(): void {
    if (this.preWarmRefreshTimer) {
      clearInterval(this.preWarmRefreshTimer);
      this.preWarmRefreshTimer = null;
    }
    this.connectionManager.destroyPreWarmedConnection();
    console.log('[PeerCoordinator] Pre-warm cycle stopped');
  }

  /**
   * Select the appropriate shadowcam based on client metadata.
   * Returns the desktop cam by default, mobile cam for mobile clients.
   */
  public selectShadowcam(metadata?: PeerMetadata): string {
    if (metadata?.playjectorSession?.shadowCamName) {
      return metadata.playjectorSession.shadowCamName;
    }
    if (metadata?.isMobile) {
      return MOBILE_SHADOWCAM;
    }
    return DESKTOP_SHADOWCAM;
  }

  private async refreshPreWarm(): Promise<void> {
    const creds = await fetchIceServersFromApi();
    if (!creds) {
      console.warn('[PeerCoordinator] Pre-warm skipped: ICE server fetch returned null');
      return;
    }
    this.connectionManager.setIceConfig({
      iceServers: [creds],
      iceTransportPolicy: this.iceTransportPolicy,
    });
    this.connectionManager.preWarm();
    console.log('[PeerCoordinator] Pre-warm refreshed');
  }

  /**
   * Trigger an immediate re-warm after the current pre-warmed connection is consumed.
   * Does not wait for the next interval tick.
   */
  private triggerImmediateReWarm(): void {
    this.refreshPreWarm().catch((err) =>
      console.warn('[PeerCoordinator] Immediate re-warm failed:', err),
    );
  }

  // ========================================
  // Public API - Getters
  // ========================================

  public getPlayerCount(): number {
    return this.peerStore.currentState.playerCount;
  }

  public getPlayers(): Player[] {
    return Object.values(this.peerStore.currentState.players);
  }

  public getPlayer(playerId: string): Player | undefined {
    return this.peerStore.currentState.players[playerId];
  }

  public getConnectedPeers(): ConnectedPeer[] {
    return Object.values(this.peerStore.currentState.peers);
  }

  public getConnectedPeer(peerId: string): ConnectedPeer | undefined {
    return this.peerStore.currentState.peers[peerId];
  }

  public getPeerPlayjectorSession(
    peerId: string,
  ): NonNullable<PeerMetadata['playjectorSession']> | undefined {
    return this.getConnectedPeer(peerId)?.metadata.playjectorSession;
  }

  public setGameInstanceSessionResolver(resolver: GameInstanceSessionResolver | null): void {
    this.sessionResolver = resolver;
  }

  /**
   * Wire per-session occupancy tracking (MatchLifecycleManager in production).
   * When unset, session capture stops on any player disconnect (legacy
   * single-player-per-session behavior).
   */
  public setGameInstanceOccupancyTracker(tracker: GameInstanceOccupancyTracker | null): void {
    this.occupancyTracker = tracker;
  }

  /**
   * Wire the instance-index → HOST peer id resolver (GameInstancePeerManager
   * in production). Session-bound connections signal FROM their instance peer,
   * never from the v1 global host peer.
   */
  public setGameInstancePeerIdResolver(
    resolver: ((gameInstanceIndex: number) => string | undefined) | null,
  ): void {
    this.gameInstancePeerIdResolver = resolver;
  }

  /**
   * Register an instance-specific HOST peer. Call when an instance transitions
   * to `ready` and the API assigns it a peerId via `PEER_GET_ID`.
   */
  public registerInstancePeer(peerId: string, gameInstanceIndex: number): void {
    this.instancePeerIds.set(peerId, gameInstanceIndex);
  }

  /**
   * Deregister an instance-specific HOST peer. Call when an instance leaves
   * the `ready` state or is torn down.
   */
  public deregisterInstancePeer(peerId: string): void {
    this.instancePeerIds.delete(peerId);
  }

  /** Get the game instance index for a registered instance peer. */
  public getInstanceIndexForPeer(peerId: string): number | undefined {
    return this.instancePeerIds.get(peerId);
  }

  /** Get all registered instance peer IDs. */
  public getInstancePeerIds(): ReadonlyMap<string, number> {
    return this.instancePeerIds;
  }

  public getPeersByType(peerType: PeerType): ConnectedPeer[] {
    return this.getConnectedPeers().filter((p) => p.peerType === peerType);
  }

  public getHostPeerId(): string | undefined {
    return this.peerStore.currentState.hostPeerId;
  }

  public getRemoteHostPeerId(): string | undefined {
    return this.peerStore.currentState.remoteHostPeerId;
  }

  public setBitrateOverride(bitrateBps: number | null): void {
    this.connectionManager.setBitrateOverride(bitrateBps);
  }

  public getBitrateOverride(): number | null {
    return this.connectionManager.getBitrateOverride();
  }

  // ========================================
  // Public API - Peer Lifecycle
  // ========================================

  /**
   * Accept an incoming SDP offer from a remote peer.
   * Reads peerMetadata to select tracks and permissions before answering.
   */
  public async acceptIncomingPeer(
    peerId: string,
    peerType: PeerType,
    metadata: PeerMetadata,
    sdp: RTCSessionDescriptionInit,
    videoTrack: MediaStreamTrack | null,
    audioTrack: MediaStreamTrack | null,
  ): Promise<void> {
    this.logAcceptIncomingPeerBreadcrumb(peerId, peerType, videoTrack, audioTrack);
    // v2: a player who offered to an instance peer is answered FROM that
    // instance peer. The 'signal' events fire during connection.signal(sdp)
    // below — before the peer lands in the store — so the fromId is resolved
    // here, at accept time, from the offer metadata.
    const instanceIndex = metadata.playjectorSession?.gameInstanceIndex;
    const instancePeerId = instanceIndex !== undefined
      ? this.gameInstancePeerIdResolver?.(instanceIndex)
      : undefined;
    if (instancePeerId) this.connectionFromIds.set(peerId, instancePeerId);
    const consumedPreWarm = this.connectionManager.hasPreWarmedConnection();
    const connection = this.connectionManager.createHostSideConnection(
      peerId, peerType, metadata, videoTrack, audioTrack,
    );
    await connection.signal(sdp);
    await this.connectionManager.drainPendingIceCandidates(peerId);

    if (consumedPreWarm) {
      this.triggerImmediateReWarm();
    }
  }

  public async kickPeer(peerId: string, reason = 'host:kick'): Promise<void> {
    console.log(`[PeerCoordinator] Kicking peer: ${peerId} (${reason})`);
    this.sendCloseReason(peerId, reason);
    await this.delayThenDestroy(peerId);
    this.removePeerFromStore(peerId);
  }

  public disconnectAllPeers(reason = 'host:close'): void {
    const peerIds = Object.keys(this.peerStore.currentState.peers);
    for (const peerId of peerIds) {
      this.sendCloseReason(peerId, reason);
    }
    setTimeout(() => {
      this.connectionManager.destroyAll();
      for (const peerId of peerIds) {
        this.removePeerFromStore(peerId);
      }
    }, 100);
  }

  private sendCloseReason(peerId: string, reason: string): void {
    try {
      const msg = JSON.stringify({ __peerClose: true, reason });
      this.connectionManager.sendData(peerId, msg);
    } catch {
      // Connection may already be gone
    }
  }

  private delayThenDestroy(peerId: string): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.connectionManager.destroyConnection(peerId);
        resolve();
      }, 100);
    });
  }

  // ========================================
  // Public API - Permissions
  // ========================================

  public getPeerPermissions(peerId: string): PeerPermissions | undefined {
    return this.peerStore.currentState.peers[peerId]?.permissions;
  }

  public updatePeerPermissions(peerId: string, permissions: Partial<PeerPermissions>): void {
    const peer = this.peerStore.currentState.peers[peerId];
    if (!peer) return;

    const updated = { ...peer.permissions, ...permissions };
    const updatedPeer = { ...peer, permissions: updated };
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      peers: { ...this.peerStore.currentState.peers, [peerId]: updatedPeer },
    });
    this.events.permissionsChanged.emit({ peerId, permissions: updated });
  }

  public updateAllPlayerPermissions(permissions: Partial<PeerPermissions>): void {
    for (const peer of this.getPeersByType('player')) {
      this.updatePeerPermissions(peer.peerId, permissions);
    }
  }

  // ========================================
  // Public API - Input Control (delegates to InputControlManager)
  // ========================================

  public registerInputStores(stores: InputConfigStores): void { this.inputControl.registerInputStores(stores); }
  public registerGamepadManager(manager: GamepadManager): void { this.inputControl.registerGamepadManager(manager); }
  public registerActivityTracker(tracker: ActivityTracker): void { this.inputControl.registerActivityTracker(tracker); }

  public setPeerPaused(peerId: string, paused: boolean): void {
    if (!this.getPlayer(peerId)) return;
    this.inputControl.setPeerPaused(peerId, paused);
  }
  public setGlobalPaused(paused: boolean): void { this.inputControl.setGlobalPaused(paused); }
  public isPeerPaused(peerId: string): boolean { return this.inputControl.isPeerPaused(peerId); }
  public isGloballyPaused(): boolean { return this.inputControl.isGloballyPaused(); }

  public assignGamepadSlot(peerId: string, slotIndex: number): void { this.inputControl.assignGamepadSlot(peerId, slotIndex); }
  public removeGamepadSlot(peerId: string, slotIndex: number): void { this.inputControl.removeGamepadSlot(peerId, slotIndex); }
  public getGamepadSlots(peerId: string): PlaycastGamepadId[] { return this.inputControl.getGamepadSlots(peerId); }
  public getGamepadConfig(): GamepadConfigState { return this.inputControl.getGamepadConfig(); }
  public updateGamepadConfig(config: Partial<GamepadConfigState>): void { this.inputControl.updateGamepadConfig(config); }
  public setGamepadAccess(peerId: string, enabled: boolean): void { this.updatePeerPermissions(peerId, { gamepad: enabled }); }

  public getPeerStats(peerId: string): PeerStats | undefined { return this.inputControl.getPeerStats(peerId); }
  public getAllPeerStats(): PeerStats[] { return this.inputControl.getAllPeerStats(); }

  public isPlayerAfk(peerId: string): boolean { return this.inputControl.isPlayerAfk(peerId); }

  // ========================================
  // Public API - Peer ID Management (legacy bridge)
  // ========================================

  public updateHostPeerId(peerId: string | undefined): void {
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      hostPeerId: peerId,
    });
    this.events.hostPeerIdChanged.emit(peerId);
  }

  public updateRemoteHostPeerId(peerId: string | undefined): void {
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      remoteHostPeerId: peerId,
    });
    this.events.remoteHostPeerIdChanged.emit(peerId);
  }

  // ========================================
  // Public API - Data Channel
  // ========================================

  public sendToPeer(peerId: string, data: string | ArrayBuffer): boolean {
    return this.connectionManager.sendData(peerId, data);
  }

  public broadcastToAll(data: string | ArrayBuffer): void {
    this.connectionManager.broadcastData(data);
  }

  // ========================================
  // Public API - Registry access (for extending)
  // ========================================

  public getTypeRegistry(): PeerTypeRegistry {
    return this.typeRegistry;
  }

  // ========================================
  // Legacy Bridge - called by legacy code during migration
  // ========================================

  public onPlayerJoined(playerId: string, playerName: string): void {
    const player: Player = { playerId, playerName, joinedAt: Date.now() };
    const newPlayers = { ...this.peerStore.currentState.players, [playerId]: player };
    const newPlayerCount = Object.keys(newPlayers).length;

    this.peerStore.updateState({
      ...this.peerStore.currentState,
      players: newPlayers,
      playerCount: newPlayerCount,
    });

    this.events.onPlayerJoined.emit({ peerId: playerId, metadata: {} });
    this.events.playerCountChanged.emit(newPlayerCount);
  }

  public onPlayerLeft(playerId: string): void {
    const player = this.peerStore.currentState.players[playerId];
    if (!player) return;

    const newPlayers = { ...this.peerStore.currentState.players };
    delete newPlayers[playerId];
    const newPlayerCount = Object.keys(newPlayers).length;

    this.peerStore.updateState({
      ...this.peerStore.currentState,
      players: newPlayers,
      playerCount: newPlayerCount,
    });

    this.events.onPlayerLeft.emit({ peerId: playerId });
    this.events.playerCountChanged.emit(newPlayerCount);
  }

  public clearAllPlayers(): void {
    const hadPlayers = this.peerStore.currentState.playerCount > 0;
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      players: {},
      playerCount: 0,
    });
    if (hadPlayers) {
      this.events.playerCountChanged.emit(0);
    }
  }

  // ========================================
  // Internal - Peer Connected/Disconnected
  // ========================================

  private handlePeerConnected(peerId: string, peerType: PeerType, metadata: PeerMetadata): void {
    if (peerType === 'player') {
      const existingName = this.peerNameMap.get(peerId);
      if (existingName) {
        metadata = { ...metadata, displayName: existingName };
      } else if (!metadata.displayName || metadata.displayName === 'Beacon Player') {
        this.playerCounter++;
        const assignedName = `Player ${this.playerCounter}`;
        this.peerNameMap.set(peerId, assignedName);
        metadata = { ...metadata, displayName: assignedName };
      }
    }

    const permissions = { ...DEFAULT_PERMISSIONS[peerType] };
    const connectedPeer: ConnectedPeer = {
      peerId,
      peerType,
      metadata,
      permissions,
      connectedAt: Date.now(),
    };

    this.peerStore.updateState({
      ...this.peerStore.currentState,
      peers: { ...this.peerStore.currentState.peers, [peerId]: connectedPeer },
    });

    this.typeRegistry.handleConnected(peerType, peerId, metadata);
    this.emitPeerJoinedEvent(peerType, peerId, metadata);

    if (peerType === 'player' && metadata.playjectorSession?.sessionId) {
      this.occupancyTracker?.playerJoinedSession(metadata.playjectorSession.sessionId);
    }

    const connection = this.connectionManager.getConnection(peerId);
    if (connection) {
      this.statsManager.startCollection(peerId, connection);
    }

    if (peerType === 'player') {
      this.updatePlayerCountFromPeers();
    }

    // Ensure the new viewer receives an IDR keyframe so the decoder can start
    // rendering immediately, even if the initial keyframe was lost in transit.
    setTimeout(() => {
      this.connectionManager.requestKeyframeForPeer(peerId);
    }, 300);
  }

  private handlePeerDisconnected(peerId: string, peerType: PeerType): void {
    const sessionId = this.getPeerPlayjectorSession(peerId)?.sessionId;
    this.statsManager.stopCollection(peerId);
    this.typeRegistry.handleDisconnected(peerType, peerId);
    this.removePeerFromStore(peerId);
    this.emitPeerLeftEvent(peerType, peerId);

    if (peerType === 'player') {
      if (sessionId) {
        if (this.occupancyTracker) {
          // Capture outlives individual disconnects; stop only when the
          // session's last player is gone.
          const remaining = this.occupancyTracker.playerLeftSession(sessionId);
          if (remaining <= 0) this.mediaCoordinator?.stopCastForSession(sessionId);
        } else {
          this.mediaCoordinator?.stopCastForSession(sessionId);
        }
      }
      this.updatePlayerCountFromPeers();
    }
  }

  private removePeerFromStore(peerId: string): void {
    this.connectionFromIds.delete(peerId);
    const peers = { ...this.peerStore.currentState.peers };
    delete peers[peerId];
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      peers,
    });
  }

  private updatePlayerCountFromPeers(): void {
    const playerCount = this.getPeersByType('player').length;
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      playerCount,
    });
    this.events.playerCountChanged.emit(playerCount);
  }

  // ========================================
  // Internal - Peer ID & ICE Configuration
  // ========================================

  private handlePeerIdAssigned(data: PeerIdAssignedData): void {
    const { peerId, peerIdType, iceServers } = data;

    if (peerIdType === 'host') {
      this.updateHostPeerId(peerId);
    } else if (peerIdType === 'remoteHost') {
      this.updateRemoteHostPeerId(peerId);
    }

    if (iceServers.length > 0) {
      this.connectionManager.setIceConfig({
        iceServers,
        iceTransportPolicy: this.iceTransportPolicy,
      });
      console.log(`[PeerCoordinator] ICE config updated (${iceServers.length} servers, policy=${this.iceTransportPolicy})`);

      if (!this.connectionManager.hasPreWarmedConnection()) {
        this.connectionManager.preWarm();
      }
    }
  }

  // ========================================
  // Internal - Signaling
  // ========================================

  private handleSignalGenerated(peerId: string, description: RTCSessionDescriptionInit): void {
    // Session-bound connections signal from their instance peer (recorded at
    // accept time); legacy peers fall back to the v1 global host peer.
    const fromId = this.connectionFromIds.get(peerId) ?? this.peerStore.currentState.hostPeerId;
    if (!fromId || !this.signalingService) {
      Sentry.logger.warn(
        `[PeerCoordinator] Dropping outbound ${description.type} for ${peerId}: no host peer identity to send from`,
      );
      return;
    }

    if (description.type === 'offer') {
      this.signalingService.sendOffer(fromId, peerId, description);
    } else if (description.type === 'answer') {
      this.signalingService.sendAnswer(fromId, peerId, description);
    }
  }

  private handleIceCandidatesGenerated(peerId: string, candidates: RTCIceCandidate[]): void {
    const fromId = this.connectionFromIds.get(peerId) ?? this.peerStore.currentState.hostPeerId;
    if (!fromId || !this.signalingService) {
      Sentry.logger.warn(
        `[PeerCoordinator] Dropping outbound ICE candidates for ${peerId}: no host peer identity to send from`,
      );
      return;
    }

    this.signalingService.sendIceCandidate(fromId, peerId, candidates);
  }

  private async handleSignalingMessage(msg: { type: string; fromId: string; toId: string; sdp?: RTCSessionDescriptionInit; candidates?: RTCIceCandidateInit[]; peerMetadata?: PeerMetadata; peerIdType?: string }): Promise<void> {
    Sentry.logger.info(
      `[PeerCoordinator] handleSignalingMessage type=${msg.type} from=${msg.fromId} to=${msg.toId} peerIdType=${msg.peerIdType ?? 'none'}`,
    );
    if (msg.type === 'offer' && msg.sdp) {
      let metadata: PeerMetadata = msg.peerMetadata ?? {};
      const peerType = this.mapPeerIdTypeToPeerType(msg.peerIdType) ?? 'player';

      if (peerType === 'player' && !metadata.playjectorSession && this.sessionResolver) {
        const instanceIndex = this.instancePeerIds.get(msg.toId);
        Sentry.logger.info(
          `[PeerCoordinator] Instance lookup: toId=${msg.toId} instanceIndex=${instanceIndex ?? 'not-found'} registeredPeers=${this.instancePeerIds.size}`,
        );
        if (instanceIndex !== undefined) {
          const session = this.sessionResolver.getSessionForInstance(instanceIndex);
          if (!session) {
            Sentry.logger.warn(
              `[PeerCoordinator] Refusing ${msg.fromId}: no ready session for instance ${instanceIndex}`,
            );
            return;
          }
          Sentry.logger.info(
            `[PeerCoordinator] Resolved session for instance ${instanceIndex}: sessionId=${session.sessionId}`,
          );
          metadata = { ...metadata, playjectorSession: session };
        }
      }

      // Race guard: matchmakeable=false may not have propagated before the player was matched.
      if (this.refuseOfferIfShadowcamUnhealthy(msg.fromId, peerType)) {
        return;
      }

      await this.ensureMediaCapture(metadata, peerType);

      const sessionId = metadata.playjectorSession?.sessionId;
      const videoTrack = sessionId
        ? this.mediaCoordinator?.getVideoTrackForSession(sessionId) ?? null
        : this.mediaCoordinator?.getVideoTrack() ?? null;
      const audioTrack = sessionId
        ? this.mediaCoordinator?.getAudioTrackForSession(sessionId) ?? null
        : this.mediaCoordinator?.getAudioTrack() ?? null;
      const audioDetails = audioTrack
        ? { label: audioTrack.label, readyState: audioTrack.readyState, enabled: audioTrack.enabled, muted: audioTrack.muted }
        : null;
      Sentry.logger.info(
        `[PeerCoordinator] Incoming offer from=${msg.fromId} type=${peerType} hasVideo=${!!videoTrack} hasAudio=${!!audioTrack} sessionId=${sessionId ?? 'none'}`,
      );
      Sentry.addBreadcrumb({
        category: 'AUDIO-BUG-12009',
        message: `Offer received, sending tracks to peer`,
        level: audioTrack ? 'info' : 'warning',
        data: { fromId: msg.fromId, peerType, hasVideo: !!videoTrack, hasAudio: !!audioTrack, ...audioDetails },
      });

      await this.acceptIncomingPeer(msg.fromId, peerType, metadata, msg.sdp, videoTrack, audioTrack);

      this.scheduleTrackSwapIfNeeded(metadata).catch((err) =>
        Sentry.logger.error(`[PeerCoordinator] Track swap failed: ${err}`),
      );
    } else if (msg.type === 'answer' && msg.sdp) {
      Sentry.logger.info(`[PeerCoordinator] SDP answer from=${msg.fromId}`);
      this.connectionManager.handleRemoteSdp(msg.fromId, msg.sdp);
    } else if (msg.type === 'ice-candidate' && msg.candidates) {
      Sentry.logger.info(`[PeerCoordinator] ICE candidates from=${msg.fromId} count=${msg.candidates.length}`);
      for (const candidateInit of msg.candidates) {
        this.connectionManager.handleRemoteIceCandidate(
          msg.fromId,
          new RTCIceCandidate(candidateInit),
        );
      }
    }
  }

  // ========================================
  // Internal - Media Capture
  // ========================================

  private async ensureMediaCapture(metadata?: PeerMetadata, peerType?: PeerType): Promise<void> {
    if (!this.mediaCoordinator) {
      console.warn('[PeerCoordinator] No MediaCoordinator available for capture');
      return;
    }

    const session = metadata?.playjectorSession;
    const sessionId = session?.sessionId;
    const hasVideo = sessionId
      ? !!this.mediaCoordinator.getVideoTrackForSession(sessionId)
      : !!this.mediaCoordinator.getVideoTrack();
    const hasAudio = sessionId
      ? !!this.mediaCoordinator.getAudioTrackForSession(sessionId)
      : !!this.mediaCoordinator.getAudioTrack();

    if (peerType === 'admin') {
      if (this.beforeAdminMediaCapture) {
        console.log('[PeerCoordinator] Admin session: waiting for ShadowCam pipeline refresh before capture');
        await this.beforeAdminMediaCapture();
      }
      console.log(`[PeerCoordinator] Admin session: capturing ${ADMIN_SHADOWCAM} (desktop virtual display)`);
      await this.ensureActivation(ADMIN_SHADOWCAM);
      const adminVideoResult = await this.mediaCoordinator.startShadowcamCapture(ADMIN_SHADOWCAM);
      this.logVideoCaptureResult(adminVideoResult, 'admin', ADMIN_SHADOWCAM);
      if (!hasAudio) {
        console.log(`${DIAG} Capturing AudioCaster (game audio) for admin...`);
        const audioResult = await this.mediaCoordinator.startGameAudioCapture();
        this.logAudioCaptureResult(audioResult, 'admin');
      }
      console.log('[PeerCoordinator] Media capture complete (admin)', {
        hasVideo: !!this.mediaCoordinator.getVideoTrack(),
        hasAudio: !!this.mediaCoordinator.getAudioTrack(),
      });
      return;
    }

    if (sessionId) {
      // Android multi-instance host Phase 5: on the android path the instance's
      // camera is activated BY capture (streams model) — the host must not send
      // explicit bind/activate messages for it. Legacy/mumu sessions keep the
      // explicit activation below.
      const androidStreamActive = this.getActiveAndroidStreamId?.() !== undefined;
      let activationReady = true;
      if (session.shadowCamName && !androidStreamActive) {
        activationReady = await this.ensureActivation(session.shadowCamName);
      }
      const result = await this.mediaCoordinator.startCastForSession(sessionId, {
        shadowCamName: session.shadowCamName,
        audioDeviceName: session.audioDeviceName,
      });
      if (session.shadowCamName) {
        this.logVideoCaptureResult(
          result.videoTrack,
          peerType ?? 'player',
          session.shadowCamName,
        );
      }
      if (!activationReady || result.readiness.status === 'DRIVER_BLOCKED') {
        console.warn(
          `[PeerCoordinator] Game instance session ${sessionId} is not ready: ` +
          `${result.readiness.status === 'DRIVER_BLOCKED'
            ? result.readiness.reason
            : 'ShadowCam activation timed out'}`,
        );
      }
      if (result.audio.status === 'DRIVER_BLOCKED') {
        console.warn(`[PeerCoordinator] Game instance audio unavailable for ${sessionId}: ${result.audio.reason}`);
      }
      return;
    }

    if (hasVideo && hasAudio) {
      console.log('[PeerCoordinator] Tracks already captured, skipping');
      return;
    }

    if (!hasVideo) {
      const shadowcam = this.selectShadowcam(metadata);
      console.log(`[PeerCoordinator] Capturing shadowcam (${shadowcam})...`);
      await this.ensureActivation(shadowcam);
      const videoResult = await this.mediaCoordinator.startShadowcamCapture(shadowcam);
      this.logVideoCaptureResult(videoResult, peerType ?? 'unknown', shadowcam);
    }

    if (!hasAudio) {
      console.log(`${DIAG} Capturing AudioCaster (game audio) for ${peerType ?? 'unknown'}...`);
      const audioResult = await this.mediaCoordinator.startGameAudioCapture();
      this.logAudioCaptureResult(audioResult, peerType ?? 'unknown');
    }

    console.log('[PeerCoordinator] Media capture complete', {
      hasVideo: !!this.mediaCoordinator.getVideoTrack(),
      hasAudio: !!this.mediaCoordinator.getAudioTrack(),
    });
  }

  /**
   * After a peer connection is accepted, swap to the correct shadowcam if needed.
   * Uses replaceTrack() which preserves all sender parameters (bitrate, codec, etc.)
   * without renegotiation.
   */
  private async scheduleTrackSwapIfNeeded(metadata?: PeerMetadata): Promise<void> {
    if (!this.mediaCoordinator) return;
    if (metadata?.playjectorSession) return;

    const neededShadowcam = this.selectShadowcam(metadata);
    const current = this.mediaCoordinator.getCurrentShadowcamName();
    if (!current || current === neededShadowcam) return;

    await this.ensureActivation(neededShadowcam);

    const oldTrack = this.mediaCoordinator.getVideoTrack();
    console.log(`[PeerCoordinator] Swapping shadowcam: ${current} → ${neededShadowcam} via replaceTrack`);
    const newTrack = await this.mediaCoordinator.startShadowcamCapture(neededShadowcam);
    if (!newTrack || !oldTrack) return;

    await this.connectionManager.replaceTrackOnAll(oldTrack, newTrack);
    setTimeout(() => {
      this.connectionManager.requestKeyframeForAllPeers();
    }, 300);
  }

  /**
   * Activate a hub route and wait for enumeration.
   * Legacy pavilion/admin flows intentionally degrade on failure; Game instance callers
   * retain the boolean to report a structured readiness failure.
   */
  private async ensureActivation(shadowcamName: string): Promise<boolean> {
    if (this.activatedShadowcams.has(shadowcamName)) return true;
    if (!this.activateShadowcam) return true;

    this.activatedShadowcams.add(shadowcamName);
    try {
      await this.activateShadowcam(shadowcamName);
      return true;
    } catch (err) {
      this.activatedShadowcams.delete(shadowcamName);
      console.error(`[PeerCoordinator] Shadowcam activation failed: ${shadowcamName}`, err);
      return false;
    }
  }

  private logAudioCaptureResult(track: MediaStreamTrack | null, peerType: string): void {
    if (track) {
      console.log(`${DIAG} Audio capture SUCCESS for ${peerType}`, {
        label: track.label, readyState: track.readyState, enabled: track.enabled,
      });
    } else {
      console.error(`${DIAG} Audio capture FAILED for ${peerType} — track is null`);
      Sentry.captureMessage('[AUDIO-BUG-12009] Game audio capture returned null', {
        level: 'warning',
        tags: { audio_stage: 'ensure_capture', audio_diag: 'AUDIO-BUG-12009', peer_type: peerType },
      });
    }
  }

  /** Video-pipeline mirror of logAudioCaptureResult. */
  private logVideoCaptureResult(
    track: MediaStreamTrack | null,
    peerType: string,
    shadowcam: string,
  ): void {
    if (track) {
      console.log(`[BLUE-STREAM-12010:PeerCoordinator] Shadowcam capture SUCCESS for ${peerType}`, {
        shadowcam, label: track.label, readyState: track.readyState, enabled: track.enabled,
      });
      return;
    }
    console.error(`[BLUE-STREAM-12010:PeerCoordinator] Shadowcam capture FAILED for ${peerType} — track is null`);
    Sentry.captureMessage('[BLUE-STREAM-12010] Shadowcam capture returned null', {
      level: 'error',
      tags: {
        shadowcam_diag: 'BLUE-STREAM-12010',
        shadowcam_stage: 'ensure_capture',
        peer_type: peerType,
      },
      fingerprint: ['shadowcam-capture-failed', peerType],
      extra: { requestedShadowcam: shadowcam },
    });
  }

  /** Returns true when the offer was refused (caller must abort). */
  private refuseOfferIfShadowcamUnhealthy(peerId: string, peerType: PeerType): boolean {
    // BYPASS: Temporarily allow offers even when shadowcams are unhealthy
    // to unblock beacon mode streaming (no shadowcam hardware required).
    const monitor = this.mediaCoordinator?.getShadowcamPresenceMonitor();
    const health = monitor?.getHealth();
    if (!health || health.healthy) {
      return false;
    }

    console.warn(
      `[BLUE-STREAM-12010:PeerCoordinator] Shadowcams unhealthy but BYPASSED for ${peerId} (${peerType})`,
      health,
    );
    return false;
  }

  /** Provisioning snapshot breadcrumb so downstream Sentry events carry device context. */
  private logAcceptIncomingPeerBreadcrumb(
    peerId: string,
    peerType: PeerType,
    videoTrack: MediaStreamTrack | null,
    audioTrack: MediaStreamTrack | null,
  ): void {
    const deviceConfig = this.mediaCoordinator?.getDeviceConfig();
    const counts = deviceConfig?.getEnumeratedDeviceCounts() ?? {
      audioinput: 0,
      videoinput: 0,
      audiooutput: 0,
    };
    Sentry.addBreadcrumb({
      category: 'BLUE-STREAM-12010',
      level: 'info',
      message: 'acceptIncomingPeer device snapshot',
      data: {
        peerId,
        peerType,
        audioinput: counts.audioinput,
        videoinput: counts.videoinput,
        audiooutput: counts.audiooutput,
        shadowcams: deviceConfig?.getShadowcams().length ?? 0,
        audioCasterPresent: this.mediaCoordinator?.hasAudioCaster() ?? false,
        hasIncomingVideoTrack: !!videoTrack,
        hasIncomingAudioTrack: !!audioTrack,
      },
    });
  }

  // ========================================
  // Internal - Track Changes
  // ========================================

  private async handleTracksChanged(videoTrack: MediaStreamTrack | null, audioTrack: MediaStreamTrack | null): Promise<void> {
    console.log('[PeerCoordinator] Tracks changed from MediaCoordinator', {
      hasVideo: !!videoTrack,
      hasAudio: !!audioTrack,
    });

    let replaced = false;

    if (videoTrack && this.previousVideoTrack && this.previousVideoTrack !== videoTrack) {
      console.log('[PeerCoordinator] Video track changed, replacing on all peers');
      await this.connectionManager.replaceTrackOnAll(this.previousVideoTrack, videoTrack);
      replaced = true;
    }

    if (audioTrack && this.previousAudioTrack && this.previousAudioTrack !== audioTrack) {
      console.log('[PeerCoordinator] Audio track changed, replacing on all peers');
      await this.connectionManager.replaceTrackOnAll(this.previousAudioTrack, audioTrack);
      replaced = true;
    }

    if (videoTrack) this.previousVideoTrack = videoTrack;
    if (audioTrack) this.previousAudioTrack = audioTrack;

    if (replaced) {
      setTimeout(() => {
        this.connectionManager.requestKeyframeForAllPeers();
      }, 300);
    }
  }

  // ========================================
  // Internal - Data Channel
  // ========================================

  private handleRemoteStream(peerId: string, stream: MediaStream | undefined): void {
    if (!stream) {
      console.log(`[PeerCoordinator] Remote stream event from ${peerId} with no stream (expected for admin peers)`);
      return;
    }
    console.log(`[PeerCoordinator] Remote stream from ${peerId}`, {
      tracks: stream.getTracks().length,
    });
  }

  private handleDataChannelMessage(peerId: string, data: string | ArrayBuffer): void {
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        this.notifyTypeHandler(peerId, parsed);
        this.events.dataReceived.emit({ peerId, data: parsed });
      } catch {
        console.warn(`[PeerCoordinator] Invalid JSON from peer ${peerId}`);
      }
    } else {
      this.events.dataReceived.emit({ peerId, data });
    }
  }

  private notifyTypeHandler(peerId: string, message: unknown): void {
    const peer = this.peerStore.currentState.peers[peerId];
    if (!peer) return;

    const raw = message as Record<string, unknown>;
    const header = raw?.header as Record<string, unknown> | undefined;
    const serializable: SerializableMessage = {
      type: (header?.target as string) ?? 'unknown',
      payload: raw,
    };
    this.typeRegistry.handleMessage(peer.peerType, peerId, serializable);
  }

  // ========================================
  // Internal - Event Emission
  // ========================================

  private readonly joinedEventMap: Record<PeerType, CoordinatorEventImpl<{ peerId: string; metadata: PeerMetadata }>> = {
    player: this.events.onPlayerJoined as CoordinatorEventImpl<{ peerId: string; metadata: PeerMetadata }>,
    admin: this.events.onAdminJoined as CoordinatorEventImpl<{ peerId: string; metadata: PeerMetadata }>,
    remoteHost: this.events.onRemoteHostJoined as CoordinatorEventImpl<{ peerId: string; metadata: PeerMetadata }>,
  };

  private readonly leftEventMap: Record<PeerType, CoordinatorEventImpl<{ peerId: string }>> = {
    player: this.events.onPlayerLeft as CoordinatorEventImpl<{ peerId: string }>,
    admin: this.events.onAdminLeft as CoordinatorEventImpl<{ peerId: string }>,
    remoteHost: this.events.onRemoteHostLeft as CoordinatorEventImpl<{ peerId: string }>,
  };

  private emitPeerJoinedEvent(peerType: PeerType, peerId: string, metadata: PeerMetadata): void {
    this.joinedEventMap[peerType]?.emit({ peerId, metadata });
  }

  private emitPeerLeftEvent(peerType: PeerType, peerId: string): void {
    this.leftEventMap[peerType]?.emit({ peerId });
  }

  // ========================================
  // Internal - Type Registration
  // ========================================

  private registerDefaultHandlers(): void {
    const peerTypes: PeerType[] = ['player', 'admin', 'remoteHost'];
    for (const type of peerTypes) {
      this.typeRegistry.register(type, {
        onConnected: (peerId, metadata) => console.log(`[PeerCoordinator] ${type}: connected ${peerId}`, metadata),
        onDisconnected: (peerId) => console.log(`[PeerCoordinator] ${type}: disconnected ${peerId}`),
        onMessage: (peerId, message) => console.log(`[PeerCoordinator] ${type}: message from ${peerId}`, message.type),
      });
    }
  }

  private mapPeerIdTypeToPeerType(peerIdType: string | undefined): PeerType | null {
    switch (peerIdType) {
      case 'player':
        return 'player';
      case 'admin':
        return 'admin';
      case 'remoteHost':
      case 'remoteClient':
        return 'remoteHost';
      default:
        return null;
    }
  }
}
